/** Adventure pathfinding: one Dijkstra pass over the 8-neighbourhood, plus the
 * portal edges that join the two z-planes into a single graph.
 *
 * The pass is per hero per turn and the result is cached by the caller, outside
 * `GameState` - a `ReachableSet` is a pure function of (map, origin, rules,
 * links), so it is derived data and putting it in state would only make saves
 * bigger and hashing slower (Section 2, I11).
 *
 * Dijkstra and not A*: the UI wants the whole reachable region shaded, not one
 * route, and the map is small enough that a full pass is cheaper than one A*
 * per hovered tile.
 *
 * There is no vertical adjacency on a classic map: the only way from the surface
 * to the underground is a subterranean gate, and the only way between two
 * distant points on one plane can be a monolith. Those are handed in as
 * `PortalLink`s rather than read off the map, because the map arrays carry an
 * object *slot* per cell and not the payload that says which mouth answers
 * which - and because the caller is the only one who knows whose eyes the pass
 * is being run for. That is what keeps I9 true: a planner running on a
 * `PlayerObservation` can only build a link it has actually seen both ends of,
 * so it cannot route through a gate whose far mouth is still black.
 */

import type { Explained, ModifierTerm } from "../modifiers.js";
import { asModifierSrcId } from "../ids.js";
import { ROAD_KINDS, TERRAINS } from "../state.js";
import type { AdventureMapState } from "../state.js";
import { FP_ONE, mulFp } from "../numeric.js";
import {
  NEIGHBOUR_DIAGONAL, NEIGHBOUR_DX, NEIGHBOUR_DY, ROAD_COST, TERRAIN_COST,
  cellCoords, cellEnterable, explainStepCost, stepCost,
} from "./create.js";
import type { MoveRules } from "./create.js";

/** Sentinel for "no path found yet"; fits Int32 and survives arithmetic. */
export const UNREACHABLE_COST = 0x7fffffff;

/**
 * One directed mouth-to-mouth jump.
 *
 * Both fields are flat cell indices into the whole map - `z * width * height +
 * y * width + x` - so a link spans planes simply by having endpoints with
 * different `z`. They are the cells a hero actually *stands* on, not object
 * anchors: a subterranean gate is a two-by-two gatehouse whose anchor is a
 * blocking footprint cell and whose entrance is at offset (1, 0), and it is the
 * entrance that goes in here. Use `arrivalCell`-equivalent geometry on the far
 * side for `to`.
 *
 * A bidirectional gate is two links. A one-way portal (`entryOnly`) is one.
 */
export interface PortalLink {
  readonly from: number;
  readonly to: number;
}

/** How far a pass is allowed to see. */
export type ReachScope = "level" | "map";

export interface ReachableOptions {
  /** Stop expanding beyond this accumulated cost. Default: the whole map. */
  readonly maxCost?: number;
  /**
   * `"map"` (the default) lets a link change plane; `"level"` keeps only links
   * whose two mouths are both on the origin's plane, which is the overlay the
   * adventure-map UI shades - "where can I get to on this level".
   *
   * With no links the two are identical, because a walk never changes plane.
   * That is why `"map"` can be the default without moving a single existing
   * result.
   */
  readonly scope?: ReachScope;
  /**
   * Portal mouths this pass may use. Order does not matter: the list is
   * canonicalised before it is walked (see `canonicalLinks`).
   */
  readonly links?: readonly PortalLink[];
  /**
   * `FogState.seen`, or any per-cell mask where 0 means "never explored". When
   * given, a link with an unexplored mouth is not an edge (I9). A shadow map
   * built from a `PlayerObservation` already zeroes `moveCost` outside explored
   * ground, so this is belt and braces there; it is the whole rule for a pass
   * run against the true map on a player's behalf.
   */
  readonly explored?: Uint8Array;
}

export interface ReachableSet {
  readonly origin: number;
  readonly cost: Int32Array;
  readonly prev: Int32Array;
  readonly reachable: Uint8Array;
  /**
   * Index into `links` of the jump that lands on this cell, or -1. A cell with
   * `via >= 0` was arrived at through a portal: `prev` is the cell the hero
   * walked *from*, and the mouth he stepped onto is `links[via].from`.
   */
  readonly via: Int32Array;
  /** Portal jumps on the recorded cheapest route to each cell. */
  readonly jumps: Int32Array;
  /** The canonical link list `via` indexes into. Empty for a walk-only pass. */
  readonly links: readonly PortalLink[];
}

/** One stretch of a route that a single `MOVE_HERO` can execute. */
export interface PathLeg {
  /**
   * Cells stepped onto, in walk order, excluding the cell the leg starts from.
   * Every pair is 8-adjacent and on one plane, which is exactly what `moveHero`
   * accepts.
   */
  readonly cells: readonly number[];
  /**
   * The jump taken by stepping onto the last cell of `cells`, or null on the
   * final leg. Stepping onto a mouth teleports and ends the day, so the hero is
   * standing on `link.to` with no movement left when this leg returns.
   */
  readonly link: PortalLink | null;
}

/**
 * One mouth of a teleporting object, as the caller sees it.
 *
 * `cell` is the cell a hero *stands* on - the object's entrance going in, its
 * arrival cell coming out, which for a two-by-two gatehouse is the anchor plus
 * the def's first entrance offset and not the anchor itself. `exitable` is
 * false for a one-way entrance (`entryOnly` in the payload): a hero may walk
 * into it, but nothing ever comes out of it.
 */
export interface PortalMouth {
  readonly channel: string;
  readonly cell: number;
  readonly exitable: boolean;
}

/**
 * Every jump a set of mouths offers: within a channel, each mouth to each other
 * exitable mouth, exactly as `portalExits` enumerates them.
 *
 * This is the bridge between "objects with payloads", which the pathfinder
 * deliberately knows nothing about, and `PortalLink`, which is all it wants.
 * Build the mouth list from whatever the caller is entitled to see - the
 * kernel from `state.objects`, a planner from its `PlayerObservation` - and the
 * pass inherits that entitlement, which is how I9 survives the shortcut.
 *
 * A channel with three or more mouths produces an ambiguous `from` and is then
 * dropped by `canonicalLinks`; that is deliberate and is documented there.
 */
export function portalLinks(mouths: readonly PortalMouth[]): readonly PortalLink[] {
  const sorted = mouths
    .filter((m) => m.channel !== "" && Number.isInteger(m.cell) && m.cell >= 0)
    .slice()
    // Ordinal, not linguistic: `localeCompare` is banned in core and would make
    // the link list depend on a locale (I3).
    .sort((a, b) => (a.channel === b.channel
      ? a.cell - b.cell
      : (a.channel < b.channel ? -1 : 1)));

  const out: PortalLink[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i + 1;
    while (j < sorted.length && sorted[j]!.channel === sorted[i]!.channel) j++;
    for (let a = i; a < j; a++) {
      for (let b = i; b < j; b++) {
        if (a === b || !sorted[b]!.exitable) continue;
        if (sorted[a]!.cell === sorted[b]!.cell) continue;
        out.push({ from: sorted[a]!.cell, to: sorted[b]!.cell });
      }
    }
    i = j;
  }
  return out;
}

/**
 * Filter and order the caller's links into the canonical list a pass walks.
 *
 * Three rules, and the third is the one that keeps the planner honest:
 *
 * 1. A mouth the hero could not stand on is not an edge - `cellEnterable` is
 *    asked about both ends, so a land hero gets no use out of a whirlpool and a
 *    gate under unexplored ground (whose `moveCost` reads 0) disappears.
 * 2. `explored`, when given, must light both mouths. This is I9.
 * 3. **A `from` that answers with more than one `to` is dropped entirely.**
 *    `objects.ts` raises a `CHOOSE` prompt for a portal with several exits and
 *    the answer is the player's, not the route planner's - so a whirlpool
 *    (every whirlpool on the map shares one channel) and any monolith with
 *    three or more mouths are skipped rather than guessed at. Paired
 *    subterranean gates and two-mouth monoliths, which is what the generator
 *    lays, are unambiguous and survive.
 *
 * The survivors are sorted by `(from, to)` ascending. That order is what `via`
 * indexes and what the tie-break below compares, so it must be a function of
 * the link *set* and never of the order the caller happened to collect them in.
 */
export function canonicalLinks(
  map: AdventureMapState, raw: readonly PortalLink[], rules: MoveRules,
  originPlane: number, scope: ReachScope, explored: Uint8Array | undefined,
): readonly PortalLink[] {
  const n = map.width * map.height * map.levels;
  const plane = map.width * map.height;
  const kept: PortalLink[] = [];
  for (const l of raw) {
    const from = l.from, to = l.to;
    if (!Number.isInteger(from) || !Number.isInteger(to)) continue;
    if (from < 0 || from >= n || to < 0 || to >= n || from === to) continue;
    if (scope === "level") {
      if (Math.trunc(from / plane) !== originPlane) continue;
      if (Math.trunc(to / plane) !== originPlane) continue;
    }
    if (!cellEnterable(map, from, rules) || !cellEnterable(map, to, rules)) continue;
    if (explored !== undefined && (explored[from] === 0 || explored[to] === 0)) continue;
    kept.push({ from, to });
  }
  kept.sort((a, b) => (a.from !== b.from ? a.from - b.from : a.to - b.to));

  // One group per `from`, in sorted order. A group of one survives; a group
  // that offers two different destinations is an unanswered CHOOSE and is
  // dropped whole rather than resolved by picking a winner.
  const out: PortalLink[] = [];
  let i = 0;
  while (i < kept.length) {
    const head = kept[i]!;
    let j = i + 1;
    let ambiguous = false;
    while (j < kept.length && kept[j]!.from === head.from) {
      if (kept[j]!.to !== head.to) ambiguous = true;
      j++;
    }
    if (!ambiguous) out.push(head);
    i = j;
  }
  return out;
}

/**
 * The dearest a single straight step can be, over every terrain and every road
 * the content can name. Every rule that touches a step price - the road pairing,
 * Pathfinding, native terrain - only ever lowers it, so this is a true ceiling
 * and not a typical value.
 */
const MAX_STRAIGHT_STEP = Math.max(
  ...TERRAINS.map((t) => TERRAIN_COST[t]), ...ROAD_KINDS.map((r) => ROAD_COST[r]),
);

/**
 * How many buckets the frontier needs: one per distinct cost a step can add,
 * plus the bucket being drained. A diagonal multiplier above 1 is the only rule
 * that makes a step dearer than `MAX_STRAIGHT_STEP`, so it is the only one that
 * widens the ring. `map.spec.ts` pins this against `stepCost` itself, because a
 * step that overflowed the ring would be silently mis-ordered rather than
 * caught.
 */
const stepSpan = (rules: MoveRules): number => (
  rules.diagonalFp > FP_ONE ? mulFp(MAX_STRAIGHT_STEP, rules.diagonalFp) : MAX_STRAIGHT_STEP
) + 1;

/**
 * Dijkstra from `origin` over walk steps and portal links.
 *
 * `cost` is the true cheapest distance in movement points and is not truncated
 * at the hero's remaining movement, so the caller can draw multi-day routes.
 * `reachable` is the subset the hero can enter *today* under `rules.budget`.
 *
 * **Portal cells are not through-cells.** Walking onto a mouth teleports you,
 * so there is no relaxation that leaves a hero standing on one: a step onto
 * `link.from` is relaxed into `link.to` instead, carrying the price of that
 * step and nothing more. That is the honest cost - the jump itself is free and
 * the far mouth is where the hero ends up - and it is also the reason a mouth
 * cell's own `cost` stays `UNREACHABLE_COST` unless the hero starts on it or
 * arrives through the link pointing at it. Ask for the far mouth; the route to
 * it ends by stepping onto the near one.
 *
 * Determinism. The canonical answer is decided by the **relaxation rule**, not
 * by the order the frontier hands cells back: a cell keeps the route that is
 * smallest by `(cost, jumps, predecessor flat index, link index)`, in that
 * order. Nothing else can break the tie, because every step costs something -
 * the cheapest ground on the map is a cobblestone road at 50 - so two cells at
 * the same distance can never relax each other, and the frontier is therefore
 * free to hand back equal-cost cells in any order it likes. A zero-priced step
 * would break that argument and nothing else about the pass, which is the one
 * thing to check before adding one.
 *
 * The flat index that decides the third level runs **surface plane first, then
 * underground, row-major within each plane** - it is `z * width * height + y *
 * width + x`, and nothing re-bases it. That is the order a gate edge is
 * compared in as well: a jump arriving from the underground has a predecessor
 * index one whole plane above a walk arriving on the surface, so a cross-plane
 * route only wins a tie when its predecessor is genuinely the smaller cell.
 * Every level here is a replay gate: `prev` decides the exact path a recorded
 * `MOVE_HERO` replays, so re-ordering the planes, sorting the links
 * differently, or letting a jump win a tie would silently change stored games.
 * Change any of it and the replay corpus has to be re-recorded.
 *
 * `jumps` sits *above* the predecessor rule and *below* cost on purpose: at
 * equal movement points the walk-only route is strictly better, because a
 * portal ends the hero's day the moment he steps into it.
 */
export function computeReachable(
  map: AdventureMapState, origin: number, rules: MoveRules, opts: ReachableOptions = {},
): ReachableSet {
  const n = map.width * map.height * map.levels;
  const cost = new Int32Array(n).fill(UNREACHABLE_COST);
  const prev = new Int32Array(n).fill(-1);
  const via = new Int32Array(n).fill(-1);
  const jumps = new Int32Array(n);
  const reachable = new Uint8Array(n);
  const plane = map.width * map.height;

  if (origin < 0 || origin >= n) {
    return { origin, cost, prev, reachable, via, jumps, links: [] };
  }

  const originPlane = Math.trunc(origin / plane);
  const rawLinks = opts.links ?? [];
  const links = rawLinks.length === 0
    ? []
    : canonicalLinks(map, rawLinks, rules, originPlane, opts.scope ?? "map", opts.explored);

  // Cell -> index into `links` for the mouth standing on it. Allocated only
  // when there is something to put in it; a walk-only pass pays nothing.
  let mouth: Int32Array | null = null;
  if (links.length > 0) {
    mouth = new Int32Array(n).fill(-1);
    for (let i = 0; i < links.length; i++) mouth[links[i]!.from] = i;
  }

  const w = map.width, h = map.height;
  const maxCost = opts.maxCost ?? UNREACHABLE_COST;

  cost[origin] = 0;
  reachable[origin] = 1;

  // Dial's bucket queue rather than a binary heap. Distances are small integers
  // and one step is priced between a cobblestone road and `MAX_STRAIGHT_STEP`
  // scaled for a diagonal, so the whole frontier always fits inside one ring of
  // `span` buckets and a pop is O(1) instead of O(log n). Measured on a 72x72
  // two-level map with sixteen gate mouths: 5.2 ms a pass on the binary heap
  // this replaces, 3.4 ms here - against 2.6 ms for the single-plane walk-only
  // pass that predates portal edges, which is what the AI used to pay.
  //
  // Order *within* a bucket is deliberately unspecified - see the determinism
  // note above. It cannot reach the result: equal-cost cells never relax each
  // other, so which of them is popped first changes nothing in `cost`, `prev`,
  // `via` or `jumps`.
  const span = stepSpan(rules);
  const buckets: number[][] = new Array<number[]>(span);
  for (let i = 0; i < span; i++) buckets[i] = [];
  let queued = 0;
  // The cost level being drained. It only ever moves forward, and a push is
  // always at least one point ahead of it, so a bucket is emptied for good
  // before the ring wraps back onto it.
  let scan = 0;

  const push = (c: number, cell: number): void => {
    buckets[c % span]!.push(cell);
    queued++;
  };

  push(0, origin);

  const settled = new Uint8Array(n);

  while (queued > 0) {
    let bucket = buckets[scan % span]!;
    while (bucket.length === 0) { scan++; bucket = buckets[scan % span]!; }
    const cur = bucket.pop()!;
    queued--;
    // A cell can sit in the queue twice, once per improvement; the later, worse
    // entry is dropped here rather than removed when its key changed.
    if (settled[cur] === 1) continue;
    settled[cur] = 1;
    const base = cost[cur]!;
    if (base >= maxCost) continue;
    const curJumps = jumps[cur]!;

    const zBase = Math.trunc(cur / plane) * plane;
    const r = cur - zBase;
    const cx = r % w, cy = Math.trunc(r / w);

    for (let d = 0; d < 8; d++) {
      const nx = cx + NEIGHBOUR_DX[d]!, ny = cy + NEIGHBOUR_DY[d]!;
      if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
      const nb = zBase + ny * w + nx;
      // A diagonal past two blocked orthogonal neighbours is legal in the classic;
      // there is no corner-cutting rule on the adventure map.
      if (!cellEnterable(map, nb, rules)) continue;

      // Stepping onto a mouth is a step onto the far side; the hero never gets
      // to stand on `nb` and walk on from it.
      const li = mouth === null ? -1 : mouth[nb]!;
      const to = li >= 0 ? links[li]!.to : nb;
      if (settled[to] === 1) continue;

      const c = base + stepCost(map, cur, nb, rules, NEIGHBOUR_DIAGONAL[d]!);
      if (c > maxCost) continue;
      const j = li >= 0 ? curJumps + 1 : curJumps;
      const oldC = cost[to]!, oldJ = jumps[to]!;
      if (c < oldC || (c === oldC && j < oldJ)) {
        cost[to] = c;
        jumps[to] = j;
        prev[to] = cur;
        via[to] = li;
        push(c, to);
      } else if (c === oldC && j === oldJ) {
        // Equal cost and equal jump count: canonicalise on the smaller
        // predecessor index, then on a walk (-1) beating a jump. No re-push is
        // needed - neither the distance nor the jump count changed, and no
        // successor's key depends on which predecessor `to` records.
        const pv = prev[to]!;
        if (cur < pv || (cur === pv && li < via[to]!)) { prev[to] = cur; via[to] = li; }
      }
    }
  }

  const budget = rules.budget;
  // A portal ends the day on arrival (`objects.ts` returns STOP), so the only
  // cell past a jump a hero can occupy today is the far mouth itself.
  for (let i = 0; i < n; i++) {
    if (cost[i]! > budget) continue;
    const j = jumps[i]!;
    if (j === 0 || (j === 1 && via[i]! >= 0)) reachable[i] = 1;
  }

  if (rules.lastStepFree && budget > 0) {
    // The classic: with at least one movement point left a hero may always take one
    // more step, however expensive. The step must start from a cell that is
    // strictly inside the budget, and it ends the hero's day. Through a portal
    // the step in question is the step onto the near mouth, so that is the cell
    // whose neighbours are searched.
    for (let i = 0; i < n; i++) {
      if (reachable[i] === 1 || cost[i] === UNREACHABLE_COST) continue;
      const j = jumps[i]!;
      if (j > 1 || (j === 1 && via[i]! < 0)) continue;
      const entry = via[i]! >= 0 ? links[via[i]!]!.from : i;
      if (!cellEnterable(map, entry, rules)) continue;
      const zBase = Math.trunc(entry / plane) * plane;
      const r = entry - zBase;
      const cx = r % w, cy = Math.trunc(r / w);
      let best = -1;
      for (let d = 0; d < 8; d++) {
        const nx = cx + NEIGHBOUR_DX[d]!, ny = cy + NEIGHBOUR_DY[d]!;
        if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
        const nb = zBase + ny * w + nx;
        if (cost[nb]! >= budget || jumps[nb]! !== 0) continue;
        // Prefer the cheapest approach, then the smaller flat index.
        if (best < 0 || cost[nb]! < cost[best]! || (cost[nb]! === cost[best]! && nb < best)) {
          best = nb;
        }
      }
      if (best >= 0) {
        reachable[i] = 1;
        prev[i] = best;
      }
    }
  }

  return { origin, cost, prev, reachable, via, jumps, links };
}

/**
 * Walk the predecessor chain back from `target`, newest first.
 *
 * Returns null on a broken or absent chain. A jump contributes two cells - the
 * mouth the hero steps onto and the mouth he arrives at - and `jumpAt` records,
 * for each entry of `cells`, the link index that landed on it, or -1.
 */
function reconstruct(
  set: ReachableSet, target: number,
): { cells: number[]; jumpAt: number[] } | null {
  if (target === set.origin) return { cells: [], jumpAt: [] };
  if (target < 0 || target >= set.cost.length) return null;
  if (set.cost[target] === UNREACHABLE_COST && set.prev[target] === -1) return null;

  const cells: number[] = [];
  const jumpAt: number[] = [];
  let cur = target;
  // The chain is acyclic by construction; the length guard turns a corrupted
  // set into an empty path instead of a hang.
  for (let guard = 0; guard <= set.cost.length; guard++) {
    if (cur === set.origin) {
      cells.reverse(); jumpAt.reverse();
      return { cells, jumpAt };
    }
    const v = set.via[cur] ?? -1;
    cells.push(cur);
    jumpAt.push(v);
    if (v >= 0) {
      const link = set.links[v];
      if (link === undefined) return null;
      cells.push(link.from);
      jumpAt.push(-1);
    }
    const p = set.prev[cur]!;
    if (p < 0) return null;
    cur = p;
  }
  return null;
}

/**
 * The flat cell indices a hero steps onto, origin excluded, in walk order.
 * Empty when `target` is unreachable or is the origin - a `MOVE_HERO` with an
 * empty path is a no-op rather than an error.
 *
 * A route through a portal contains both mouths, in order, and is therefore not
 * 8-adjacent throughout. It is still safe to hand to `MOVE_HERO`: stepping onto
 * the near mouth teleports the hero and stops him, and the kernel discards the
 * rest. Use `pathLegs` when the caller wants the days separated.
 */
export function pathTo(set: ReachableSet, target: number): number[] {
  return reconstruct(set, target)?.cells ?? [];
}

/**
 * The same route, cut at every portal. Each leg's `cells` is 8-adjacent on one
 * plane and is a legal `MOVE_HERO`; each leg but the last ends by stepping onto
 * a mouth, which spends the rest of that day.
 */
export function pathLegs(set: ReachableSet, target: number): readonly PathLeg[] {
  const built = reconstruct(set, target);
  if (built === null || built.cells.length === 0) return [];
  const out: PathLeg[] = [];
  let leg: number[] = [];
  for (let i = 0; i < built.cells.length; i++) {
    const cell = built.cells[i]!;
    const v = built.jumpAt[i]!;
    if (v >= 0) {
      // `cell` is the far mouth: the hero is put there, he does not step there.
      out.push({ cells: leg, link: set.links[v] ?? null });
      leg = [];
      continue;
    }
    leg.push(cell);
  }
  if (leg.length > 0) out.push({ cells: leg, link: null });
  return out;
}

/** Cheapest total cost to `target`, or `UNREACHABLE_COST`. */
export const pathCost = (set: ReachableSet, target: number): number =>
  target >= 0 && target < set.cost.length ? set.cost[target]! : UNREACHABLE_COST;

export const isReachable = (set: ReachableSet, target: number): boolean =>
  target >= 0 && target < set.reachable.length && set.reachable[target] === 1;

/** Portal jumps on the recorded route to `target`; 0 for a plain walk. */
export const pathJumps = (set: ReachableSet, target: number): number =>
  target >= 0 && target < set.jumps.length ? set.jumps[target]! : 0;

/** Per-step breakdown, for the movement tooltip and for rule tests. */
export const explainMoveCost = explainStepCost;

const SRC_STEP = asModifierSrcId("path.step");
const SRC_PORTAL = asModifierSrcId("path.portal");

/**
 * Total cost of a concrete path, itemised one term per step. `base` is 0: a
 * path has no intrinsic cost, only the sum of its steps.
 *
 * A hop between two cells that are not 8-adjacent is a portal jump and is
 * itemised at zero - the hero paid for the step onto the mouth on the previous
 * term and the teleport itself is free. It costs him the rest of the day, which
 * is a fact about `reachable` and not about movement points.
 */
export function explainPathCost(
  map: AdventureMapState, origin: number, path: readonly number[], rules: MoveRules,
): Explained<number> {
  const terms: ModifierTerm[] = [];
  let total = 0;
  let from = origin;
  for (const to of path) {
    const [fx, fy, fz] = cellCoords(map, from);
    const [tx, ty, tz] = cellCoords(map, to);
    if (fz !== tz || Math.abs(tx - fx) > 1 || Math.abs(ty - fy) > 1) {
      terms.push({ source: SRC_PORTAL, op: "add", value: 0, applied: true });
      from = to;
      continue;
    }
    const diagonal = fx !== tx && fy !== ty;
    const legal = cellEnterable(map, to, rules);
    const c = legal ? stepCost(map, from, to, rules, diagonal) : 0;
    terms.push({ source: SRC_STEP, op: "add", value: c, applied: legal });
    total += c;
    from = to;
  }
  return { value: total, base: 0, terms };
}
